/**
 * Layout primitives.
 *
 * The app had a vocabulary of controls — button, input, badge, card, meter —
 * and none of structure, so every screen invented its own. Measured on the tree
 * these replace: ten pages, four different maximum widths, three paddings and
 * four vertical rhythms for one repeated shape, fifty distinct padding values
 * and seventeen text sizes across the app. That is the mechanical cause of an
 * interface that reads as dense and unorganised, and of a page file reaching
 * 2584 lines.
 *
 * These carry no knowledge of any screen. A new screen picks a shape here
 * rather than starting from a blank page, which is what "evolutive" has to mean
 * in practice.
 */

import type { HTMLAttributes, ReactNode } from 'react';
import { useId } from 'react';
import { useDisclosedDescription } from '@/components/ui/density';
import { cn } from '@/lib/utils';

/* -------------------------------------------------------------------------- */
/* Page                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * The four widths, named by intent rather than by a Tailwind step.
 *
 * Ten screens carried ten independent choices — `max-w-3xl` through `6xl` with
 * no rule behind any of them. Naming the intent is what makes a new screen's
 * choice reviewable: "is this a reading column or a dashboard" is a question
 * with an answer, "is this 4xl or 5xl" is not.
 */
export const PAGE_WIDTHS = {
  /** One column of text and settings. */
  prose: 'max-w-3xl',
  /** A list of things, each a row. */
  list: 'max-w-4xl',
  /** The default working width. */
  standard: 'max-w-5xl',
  /** Dashboards and tables, where the extra columns earn their place. */
  wide: 'max-w-6xl',
} as const;

export type PageWidth = keyof typeof PAGE_WIDTHS;

export interface PageProps extends HTMLAttributes<HTMLDivElement> {
  width?: PageWidth;
  /**
   * Vertical rhythm between the page's own children.
   *
   * `section` follows the density token; `none` is for a screen whose single
   * child manages its own spacing, which is most of the tabbed ones.
   */
  gap?: 'section' | 'none';
}

/**
 * The bounded body of a page, without the scrolling element around it.
 *
 * Exported because a screen with a sticky tab strip cannot use `Page`: Radix
 * requires `Tabs.Root` to wrap both the list and the panels, so nothing can
 * sit between the scroller and the strip. A first attempt gave `Page` a
 * `subnav` slot for exactly that case and it does not fit — the structure
 * refuted it. This composes instead: such a screen keeps its own scroller and
 * wraps its panels in a `PageBody`, and the width still lives in one place.
 */
export function PageBody({ width = 'standard', gap = 'section', className, children }: PageProps) {
  return (
    <div
      className={cn(
        'mx-auto w-full p-gutter sm:p-6',
        PAGE_WIDTHS[width],
        gap === 'section' && 'space-y-section',
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * A scrolling page body with one width and one rhythm.
 *
 * The scroll lives here rather than on the document because `body` is
 * `overflow: hidden` in this app: the shell owns the viewport and each pane
 * scrolls on its own. That is also why a clipped control never produces a
 * document-level overflow, and why `scripts/responsive.mjs` measures the first
 * overflowing *ancestor* instead.
 */
export function Page({ width, gap, className, children, ...props }: PageProps) {
  return (
    <div className="flex-1 overflow-y-auto" {...props}>
      <PageBody width={width} gap={gap} className={className}>
        {children}
      </PageBody>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Section                                                                     */
/* -------------------------------------------------------------------------- */

export interface SectionProps {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /**
   * An icon beside the title, hidden from assistive tech.
   *
   * It sits outside the heading deliberately: text inside a heading becomes
   * part of the name a screen reader announces and part of what a document
   * outline shows, and an icon has nothing to contribute to either.
   */
  icon?: ReactNode;
  /**
   * The heading level, `h2` by default.
   *
   * A page's `h1` comes from its header, so a section sitting under it is a
   * first-rank section. Rendering an `h3` skipped a level on every screen at
   * once — 24 skips measured across both languages and all three widths — which
   * makes the heading outline wrong for anyone navigating by it.
   */
  level?: 2 | 3 | 4;
  className?: string;
  children: ReactNode;
}

/**
 * A titled block: a tinted header band over its content, enclosed.
 *
 * This began as the opposite — a band separated by a rule and deliberately
 * *not* boxed, because 138 bordered boxes meant every block carried the same
 * weight and nothing led the eye. The reasoning was sound and the result was
 * not, for a reason the count could not see: the boxes never all went. A card
 * is still right where a block is an object one acts on, so the dashboard kept
 * `MetaclaudeCard` and `AdvisorCard` boxed while the brief and the run list
 * beside them were bands — and the distinction the eye actually reads there is
 * not "object versus group", it is "finished versus unfinished". The operator
 * read it as a missing border, twice, on two different screens.
 *
 * So the rule that survives is consistency, not economy: every titled block is
 * enclosed, and `Card` is what a block *inside* one is. The header still bleeds
 * to the edges (`-mx-3 px-3` against the section's own `px-3`), which is what
 * keeps the band a band rather than a boxed title.
 */
export function Section({
  title,
  description,
  actions,
  icon,
  level = 2,
  className,
  children,
}: SectionProps) {
  const id = useId();
  const Heading = `h${level}` as const;
  // The prose follows the density, and stays reachable in both — see
  // `useDisclosedDescription`. The two halves cannot be one element: the
  // control belongs on the title's baseline, the prose under it.
  const help = useDisclosedDescription(description, typeof title === 'string' ? title : undefined);
  return (
    <section
      aria-labelledby={`${id}-title`}
      className={cn('mc-card rounded-xl border border-line bg-surface px-3 pb-3', className)}
    >
      {/*
        * A tinted band, bled to the section's edges.
        *
        * `-mx-3 px-3` cancels the section's own padding exactly, so the tint
        * reaches the border on both sides and reads as a band rather than a
        * highlighted word — while the heading inside it still lines up with
        * the content below, which is what `FLUSH_TABLE` relies on.
        *
        * `rounded-t-xl` matches the enclosure's radius; the bottom stays
        * square because the rule under it is where the content begins.
        */}
      <header className="-mx-3 flex items-start justify-between gap-4 rounded-t-xl border-b border-section-line bg-section px-3 pb-2 pt-2">
        <div className="min-w-0">
          <div className="flex items-baseline gap-2">
            {icon ? (
              <span className="shrink-0 translate-y-0.5 [&>svg]:size-4" aria-hidden>
                {icon}
              </span>
            ) : null}
            <Heading id={`${id}-title`} className="text-heading text-ink">
              {title}
            </Heading>
            {help.trigger}
          </div>
          {help.body}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-1.5">{actions}</div> : null}
      </header>
      <div className="mt-stack">{children}</div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Filter row                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * A row of filter chips that scrolls sideways rather than wrapping.
 *
 * `flex-wrap` on a filter bar is a phone defect that never shows on a desk.
 * Measured on the board at 390px in French: the assignee and kind groups wrap
 * to three rows, and because that bar sits *above* the scroller it pushes the
 * first card to 237px — 28% of a 844px screen spent before any content, the
 * worst of the ten routes and by a wide margin. Nothing reports it: no control
 * is clipped, none is covered, no ancestor overflows.
 *
 * `SystemTabs` already solved this for the section strip, with the same
 * reasoning written beside it ("a strip that wraps to two rows pushes the
 * content down on every phone"), and the rule was never carried across. Here
 * it is, once, so the next filter bar inherits it. `scripts/responsive.mjs`
 * tolerates a control outside the frame precisely when an ancestor scrolls,
 * which is what makes this a choice rather than a clipped row.
 *
 * `[&>*]:shrink-0` is not decoration, for the same reason `Grid` carries
 * `[&>*]:min-w-0`: a flex child's default is to shrink before it overflows,
 * so `flex-nowrap` alone squeezes the chips instead of scrolling them —
 * measured, `Tous les types` broke over three lines inside its own pill and
 * made the bar *taller* than the wrapping version it replaced. The row has
 * to say that its children keep their width; a caller cannot be relied on
 * to remember.
 */
export const FILTER_ROW = 'flex flex-nowrap items-center overflow-x-auto [&>*]:shrink-0';

/* -------------------------------------------------------------------------- */
/* Table                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * A table flush with the section that titles it.
 *
 * Cell padding is what keeps columns apart, so `px-4` on every cell is right
 * — except on the two that touch the edges, where it becomes an indent. Inside
 * a `<Card>` that indent lined up with the card's own padding and looked
 * deliberate; inside a `<Section>` there is no padding to line up with, so the
 * first column sits four units right of the heading above it and the last one
 * stops short of the rule. Applied to the table, not to each cell, so a new
 * column cannot forget it.
 */
export const FLUSH_TABLE =
  '[&_:is(th,td):first-child]:pl-0 [&_:is(th,td):last-child]:pr-0';

/* -------------------------------------------------------------------------- */
/* Grid                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Where the columns appear, per breakpoint.
 *
 * The first version fixed the breakpoint per column count, and not one of the
 * ten hand-written grids in the app matched it: two charts side by side need
 * `xl`, two cards need `sm`, the dashboard needs `lg`. The number of columns is
 * a property of the layout; the width at which they are worth having is a
 * property of the *content*, and only the caller knows it.
 */
const AT = {
  sm: { 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3', 4: 'sm:grid-cols-4' },
  md: { 2: 'md:grid-cols-2', 3: 'md:grid-cols-3', 4: 'md:grid-cols-4' },
  lg: { 2: 'lg:grid-cols-2', 3: 'lg:grid-cols-3', 4: 'lg:grid-cols-4' },
  xl: { 2: 'xl:grid-cols-2', 3: 'xl:grid-cols-3', 4: 'xl:grid-cols-4' },
} as const;

export interface GridProps extends HTMLAttributes<HTMLDivElement> {
  cols?: 2 | 3 | 4;
  /** The width at which the columns are worth having. */
  from?: keyof typeof AT;
}

/**
 * A responsive grid whose children may shrink.
 *
 * `[&>*]:min-w-0` is the whole reason this exists rather than a bare
 * `className="grid"`. A grid item's `min-width` is `auto`, so it refuses to go
 * below its content's minimum width: on the dashboard a card blew out to 542px
 * — 697 in French — inside a 358px column, and because the grid is
 * `overflow-x: visible` nothing scrolled. The links were simply out of reach,
 * below the fold, where the screenshot bench never looked.
 */
export function Grid({ cols = 3, from = 'lg', className, children, ...props }: GridProps) {
  return (
    <div
      className={cn('grid grid-cols-1 gap-4 [&>*]:min-w-0', AT[from][cols], className)}
      {...props}
    >
      {children}
    </div>
  );
}
